import {AgGridModel} from '@xh/hoist/cmp/ag-grid';
import {HoistModel, managed, XH} from '@xh/hoist/core';
import {VirtualGridProps} from './VirtualGrid';
import {AdvancedFilterModel, ColDef, FilterModel, GridApi, IServerSideGetRowsParams} from 'ag-grid-community';
import {debounced} from '@xh/hoist/utils/js';
import {action, observable, runInAction} from '@xh/hoist/mobx';
import {isEmpty} from 'lodash';

export class VirtualGridModel<T> extends HoistModel {
    @managed gridModel: AgGridModel;
    @observable accessor rowCount: number;
    @observable accessor hasFilter: boolean;
    @observable accessor selectedRecords: T[];

    gridDataSource = {
        getRows: async (params: IServerSideGetRowsParams) => {
            const columns = params.api.getColumnDefs().map((col: ColDef) => {
                return {colId: col.colId, type: col.cellDataType};
            });

            try {
                const response = await XH.fetchService.postJson({
                    url: this.gridProps.queryUrl,
                    body: {...params.request, columns}
                });
                params.success({rowData: response.rowData, rowCount: response.totalCount});
                runInAction(() => (this.rowCount = response.totalCount));
            } catch (e) {
                params.fail();
                XH.warningToast('unable to query data');
            }
            this.handleStoreRefresh();
        }
    };

    override onLinked(): void {
        const {showCellBorders, showHover, showRowBorders, stripeRows} = this.gridProps;
        this.gridModel = new AgGridModel({
            rowBorders: showRowBorders,
            cellBorders: showCellBorders,
            showHover,
            stripeRows
        });

        this.addReaction({
            track: () => this.gridModel.isReady,
            run: isReady => {
                if (isReady) {
                    this.gridProps.onModelReady?.(this);
                    this.restoreState();
                    this.agApi.addEventListener('selectionChanged', e => this.handleSelectedChange());
                    this.agApi.addEventListener('storeRefreshed', e => this.handleStoreRefresh());
                    this.agApi.addEventListener('rowDoubleClicked', e => this.gridProps.onRowDoubleClicked?.(e.data));
                }
            }
        });
    }

    get filters(): FilterModel {
        return this.agApi.getFilterModel();
    }

    setFilter(filter: FilterModel) {
        this.agApi.setFilterModel(filter);
    }

    @action
    setAdvanedFilter(filter: AdvancedFilterModel) {
        if (filter) {
            this.agApi.setGridOption('enableAdvancedFilter', true);
            this.agApi.setAdvancedFilterModel(filter);
        } else {
            this.agApi.setGridOption('enableAdvancedFilter', false);
            this.agApi.setAdvancedFilterModel(null);
        }

        this.hasFilter = filter != null;
    }

    refreshData() {
        this.agApi.refreshServerSide();
    }

    private restoreState() {
        const {persistKey} = this.gridProps,
            {agApi} = this;

        if (!persistKey) return;

        const state = XH.getPref(persistKey);
        if (state) this.gridModel.setState(state);

        agApi.addEventListener('columnVisible', e => this.saveGridState());
        agApi.addEventListener('columnPinned', e => this.saveGridState());
        agApi.addEventListener('columnResized', e => this.saveGridState());
        agApi.addEventListener('columnMoved', e => this.saveGridState());
        // agApi.addEventListener('filterChanged', e => this.saveGridState());
        agApi.addEventListener('sortChanged', e => this.saveGridState());
    }

    @debounced(1000)
    private saveGridState() {
        const {persistKey} = this.gridProps;
        if (!persistKey) return;
        const state = this.gridModel.getState({
            excludeExpandState: true,
            excludeFilterState: true,
            excludeMiscState: true
        });
        XH.setPref(persistKey, state);
    }

    @action
    private handleSelectedChange() {
        const rows = this.agApi.getSelectedRows();
        this.selectedRecords = rows;
    }

    private handleStoreRefresh() {
        if (isEmpty(this.selectedRecords)) return;

        const ids = this.selectedRecords.map(r =>
            this.gridProps.getRowId({
                data: r,
                context: null,
                level: null,
                api: null
            })
        );
        this.gridModel.setSelectedRowNodeIds(ids);
    }

    private get agApi(): GridApi {
        return this.gridModel.agApi;
    }

    private get gridProps(): VirtualGridProps<T> {
        return this.componentProps as VirtualGridProps<T>;
    }
}
